import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Trophy, Check, Calendar, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";
import {
  getClientFingerprint,
  isAlreadyParticipatedError,
  alreadyParticipatedMessage,
  extractErrorMessage,
} from "@/lib/fingerprint";

/**
 * PredictionMatch — public player UI for prediction_match games.
 *
 * Flow:
 *   1. Player sees list of matches with team logos + kickoff times.
 *   2. Player enters:
 *        - score for Team A + Team B (football / basketball)
 *        - OR winner (tennis)
 *      for EACH match.
 *   3. On submit → POST /public/games/<slug>/prediction-submit
 *   4. Confirmation screen. Winners are drawn by admin after the matches.
 */
export default function PredictionMatch({
  game,
  participant,
  ctasCompleted,
  receipt,
  onAlreadyParticipated,
  onSubmitted,
}) {
  const { t, i18n } = useTranslation();
  const sport = game.sport || "football";
  const matches = game.matches || [];
  const [predictions, setPredictions] = useState(() =>
    matches.reduce((acc, m) => {
      acc[m.id] =
        sport === "tennis"
          ? { winner: "" }
          : { score_a: "", score_b: "" };
      return acc;
    }, {})
  );
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadyEntered, setAlreadyEntered] = useState(false);

  const locale = i18n.language === "fr" ? "fr-FR" : "en-US";

  function setP(mid, patch) {
    setPredictions((prev) => ({ ...prev, [mid]: { ...prev[mid], ...patch } }));
  }

  const canSubmit = useMemo(() => {
    if (submitting || done) return false;
    for (const m of matches) {
      const p = predictions[m.id];
      if (!p) return false;
      if (sport === "tennis") {
        if (p.winner !== "a" && p.winner !== "b") return false;
      } else {
        if (p.score_a === "" || p.score_b === "") return false;
        if (isNaN(parseInt(p.score_a)) || isNaN(parseInt(p.score_b))) return false;
      }
    }
    return matches.length > 0;
  }, [matches, predictions, sport, submitting, done]);

  async function submit() {
    if (!canSubmit) {
      toast.error(t("play.pred_required"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        participant,
        ctas_completed: ctasCompleted || [],
        predictions: matches.map((m) => {
          const p = predictions[m.id];
          if (sport === "tennis") {
            return { match_id: m.id, winner: p.winner };
          }
          return {
            match_id: m.id,
            score_a: parseInt(p.score_a),
            score_b: parseInt(p.score_b),
          };
        }),
        fingerprint: getClientFingerprint(),
        receipt_url: receipt?.url || null,
        receipt_ocr: receipt?.ocr || null,
      };
      const { data } = await axios.post(
        `${API}/public/games/${game.slug}/prediction-submit`,
        payload
      );
      setAlreadyEntered(!!data.already_entered);
      setDone(true);
      onSubmitted?.();
      toast.success(
        data.already_entered ? t("play.pred_already") : t("play.pred_confirmed_title")
      );
    } catch (e) {
      if (isAlreadyParticipatedError(e)) {
        onAlreadyParticipated?.(alreadyParticipatedMessage(e));
      } else {
        toast.error(extractErrorMessage(e, "Error"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  /* ---------------- Confirmation screen ---------------- */
  if (done) {
    return (
      <div className="max-w-lg mx-auto text-center" data-testid="pred-confirmed">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-500/15 text-emerald-600 mb-4">
          <Check className="h-8 w-8" />
        </div>
        <h3 className="text-2xl font-bold tracking-tight mb-2">
          {alreadyEntered
            ? t("play.pred_already")
            : t("play.pred_confirmed_title")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("play.pred_confirmed_body")}
        </p>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-xs font-semibold text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-violet-500" />
          {t("play.pred_matches_count", { count: matches.length })} · {t("play.pred_badge")}
        </div>
      </div>
    );
  }

  /* ---------------- Form: one card per match ---------------- */
  return (
    <div className="space-y-5" data-testid="pred-form">
      <div className="text-center">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-violet-500/10 text-violet-700 dark:text-violet-300 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] mb-2">
          <Trophy className="h-3 w-3" />
          {t(`play.pred_sport_${sport}`)}
        </div>
        <h3 className="text-xl font-bold tracking-tight">
          {t("play.pred_title")}
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
          {t("play.pred_body")}
        </p>
      </div>

      <div className="space-y-4">
        {matches.map((m, idx) => {
          const teamA = m.team_a || {};
          const teamB = m.team_b || {};
          const kickoff = m.match_datetime
            ? new Date(m.match_datetime).toLocaleString(locale, {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "";
          return (
            <div
              key={m.id}
              className="qrise-card p-4 sm:p-5"
              data-testid={`pred-match-${idx}`}
            >
              {/* Teams row */}
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 mb-3">
                <TeamBadge team={teamA} align="right" />
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  {t("play.pred_vs")}
                </div>
                <TeamBadge team={teamB} align="left" />
              </div>
              {kickoff && (
                <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground mb-3">
                  <Calendar className="h-3 w-3" />
                  {t("play.pred_match_kickoff")} · {kickoff}
                </div>
              )}

              {/* Input */}
              {sport === "tennis" ? (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setP(m.id, { winner: "a" })}
                    className={`rounded-xl border px-3 py-2.5 text-xs font-bold transition-all ${
                      predictions[m.id]?.winner === "a"
                        ? "bg-foreground text-background border-foreground"
                        : "bg-background hover:bg-muted/50 border-border"
                    }`}
                    data-testid={`pred-pick-a-${idx}`}
                  >
                    {teamA.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => setP(m.id, { winner: "b" })}
                    className={`rounded-xl border px-3 py-2.5 text-xs font-bold transition-all ${
                      predictions[m.id]?.winner === "b"
                        ? "bg-foreground text-background border-foreground"
                        : "bg-background hover:bg-muted/50 border-border"
                    }`}
                    data-testid={`pred-pick-b-${idx}`}
                  >
                    {teamB.name}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    inputMode="numeric"
                    value={predictions[m.id]?.score_a ?? ""}
                    onChange={(e) =>
                      setP(m.id, { score_a: e.target.value })
                    }
                    className="h-12 rounded-xl text-center text-xl font-bold"
                    placeholder="0"
                    data-testid={`pred-scorea-${idx}`}
                  />
                  <span className="text-lg font-bold text-muted-foreground">
                    –
                  </span>
                  <Input
                    type="number"
                    min={0}
                    inputMode="numeric"
                    value={predictions[m.id]?.score_b ?? ""}
                    onChange={(e) =>
                      setP(m.id, { score_b: e.target.value })
                    }
                    className="h-12 rounded-xl text-center text-xl font-bold"
                    placeholder="0"
                    data-testid={`pred-scoreb-${idx}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button
        onClick={submit}
        disabled={!canSubmit}
        className="w-full h-12 rounded-xl text-sm font-bold uppercase tracking-wider bg-foreground text-background hover:bg-foreground/90"
        data-testid="pred-submit-btn"
      >
        <Trophy className="h-4 w-4 mr-2" />
        {submitting ? t("play.pred_submitting") : t("play.pred_submit")}
      </Button>
    </div>
  );
}

function TeamBadge({ team, align = "left" }) {
  return (
    <div
      className={`flex items-center gap-2 min-w-0 ${
        align === "right" ? "flex-row-reverse text-right" : ""
      }`}
    >
      {team.logo_url ? (
        <img
          src={team.logo_url}
          alt={team.name}
          className="h-10 w-10 rounded-full object-cover bg-muted shrink-0"
        />
      ) : (
        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0">
          {(team.name || "?").slice(0, 2).toUpperCase()}
        </div>
      )}
      <div className="text-sm font-semibold truncate">{team.name || "—"}</div>
    </div>
  );
}
